import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import FaqAccordion from "../legal/FaqAccordion";

const Section = styled.section`
  margin-top: 2.5rem;
  padding-top: 2rem;
  border-top: 1px solid hsl(${({ theme }) => theme.colors.border});
`;

const Intro = styled.div`
  margin-bottom: 2rem;
  font-size: 0.9375rem;
  line-height: 1.7;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  width: 100%;

  p {
    margin: 0 0 0.75rem;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const GroupTitle = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: hsl(${({ theme }) => theme.colors.foreground});
`;

const GroupSub = styled.p`
  margin: -0.5rem 0 1rem;
  font-size: 0.875rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const GROUP_META = {
  troubleshooting: {
    titleKey: "store.seo.troubleshootingTitle",
    titleDefault: "Why isn't my coupon working?",
    subKey: "store.seo.troubleshootingSub",
    subDefault: "Quick fixes before you give up on a code.",
  },
  paa: {
    titleKey: "store.seo.paaTitle",
    titleDefault: "People also ask",
    subKey: "store.seo.paaSub",
    subDefault: "Common questions about coupon codes.",
  },
  faq: {
    titleKey: "store.seo.faqTitle",
    titleDefault: "Coupon codes FAQ",
    subKey: "store.seo.faqSub",
    subDefault: "Legitimacy, exclusions, and how we verify offers.",
  },
};

function faqsByGroup(faqs, group) {
  return (faqs || [])
    .filter((f) => f.isActive !== false && (f.group || "faq") === group)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((f) => ({ q: f.question, a: f.answer }));
}

export default function StoreSeoContent({ store, localized }) {
  const { t } = useTranslation();
  const faqs = localized?.faqs || [];
  const intro = localized?.seo?.intro;

  const groups = useMemo(
    () =>
      ["troubleshooting", "paa", "faq"]
        .map((g) => ({ group: g, items: faqsByGroup(faqs, g) }))
        .filter((x) => x.items.length > 0),
    [faqs]
  );

  if (!intro && groups.length === 0) return null;

  return (
    <Section>
      {intro && (
        <Intro dangerouslySetInnerHTML={{ __html: intro.replace(/\n/g, "<br />") }} />
      )}
      {groups.map(({ group, items }) => {
        const meta = GROUP_META[group] || GROUP_META.faq;
        return (
          <div key={group} style={{ marginBottom: "2rem" }}>
            <GroupTitle>{t(meta.titleKey, meta.titleDefault)}</GroupTitle>
            <GroupSub>{t(meta.subKey, meta.subDefault)}</GroupSub>
            <FaqAccordion items={items} />
          </div>
        );
      })}
    </Section>
  );
}
